/**
 * Value Proposal Store
 * 
 * Manages the lifecycle of attribute value proposals:
 * 1. Propose - Someone proposes a value for an attribute
 * 2. Accept/Reject - The proposal is reviewed and accepted or rejected
 * 3. Lock - Once in focus list, values can be locked
 * 
 * Also handles:
 * - Accepted exceptions (unknown value, proceed anyway)
 * - Session-scoped vs global acceptances
 * - Provenance tracking (who proposed, who accepted, when, why)
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import { fetchValueReplacements } from '../../lib/valueAiService';
import type { ValueReplacement } from '../../lib/valueStatus';
import type {
  ValueProposal,
  AcceptedValue,
  AcceptedException,
  AcceptanceMethod,
  ExceptionScope,
  ProposalStatus,
  ValueKind,
  ValueAggregationMethod,
  AggregatedValueInput,
} from '../../types/prioritization/itemLifecycle';

export interface ContestationRecord {
  attributeKey: string;
  /** The scale this value was recorded on, if any. */
  scaleId?: string | null;
  /** Human-readable scale label (populated by the UI layer when available). */
  scaleLabel?: string;
  value: unknown;
  contributorCount: number;
  spread?: number;
  stdDev?: number;
  isContested: boolean;
  /** True when all inputs were numeric. */
  isNumeric: boolean;
  /** For categorical attributes: number of distinct proposed values. */
  distinctValueCount?: number;
  acceptedValueId: string;
}
import { useAuditStore } from './auditStore';
import { useEvidenceStore } from './evidenceStore';
import { useAttributesStore } from './attributesStore';
import { useAttributeGovernanceStore } from './attributeGovernanceStore';
import { resolveUserCapabilities, type CapabilityId } from '../../types/prioritization/capabilityIds';
import { useUserStore } from './userStore';
import { valuesThatCount } from '../../lib/valueScope';
import {
  fetchAll,
  insertRecord,
  isSupabaseAvailable,
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';
import { isFeatureEnabled, FEATURE_FLAGS } from '../../lib/featureFlags';
import { checkPolicyForItem } from '../../lib/authorization/storeEnforcement';

// ============================================================================
// Evidence Enforcement (WS2)
// ============================================================================

interface EvidenceCheck {
  passed: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Check if an attribute value proposal has sufficient evidence support
 */
function checkEvidenceRequirements(
  itemId: string,
  attributeKey: string
): EvidenceCheck {
  const result: EvidenceCheck = { passed: true, errors: [], warnings: [] };
  
  // Only enforce if feature flag is enabled
  if (!isFeatureEnabled(FEATURE_FLAGS.EVIDENCE_ENFORCEMENT)) {
    return result;
  }
  
  const attribute = useAttributesStore.getState().getAttributeByKey(attributeKey);
  if (!attribute) return result;
  
  // Check if evidence is required for this attribute
  const requirement = attribute.evidenceRequirement || 'optional';
  
  if (requirement === 'optional') return result;
  
  const evidenceStore = useEvidenceStore.getState();
  const evidence = evidenceStore.getEvidenceForAttribute(itemId, attributeKey);
  
  // Check minimum count
  const minCount = attribute.minimumEvidenceCount || 1;
  if (evidence.length < minCount) {
    const message = `Attribute "${attribute.label}" requires at least ${minCount} evidence item(s), found ${evidence.length}`;
    if (requirement === 'required' && attribute.blockWithoutEvidence) {
      result.passed = false;
      result.errors.push(message);
    } else if (requirement === 'required') {
      result.warnings.push(message);
    } else if (requirement === 'recommended') {
      result.warnings.push(`Recommended: ${message}`);
    }
    return result;
  }
  
  // Check acceptable types
  if (attribute.acceptableEvidenceTypes && attribute.acceptableEvidenceTypes.length > 0) {
    const hasAcceptableType = evidence.some(e => 
      attribute.acceptableEvidenceTypes!.includes(e.type)
    );
    if (!hasAcceptableType) {
      const message = `Evidence for "${attribute.label}" must be of type: ${attribute.acceptableEvidenceTypes.join(', ')}`;
      if (requirement === 'required' && attribute.blockWithoutEvidence) {
        result.passed = false;
        result.errors.push(message);
      } else {
        result.warnings.push(message);
      }
    }
  }
  
  // Check minimum reliability
  if (attribute.minimumEvidenceReliability) {
    const reliabilityOrder = ['speculative', 'anecdotal', 'single_source', 'corroborated', 'verified'];
    const minIndex = reliabilityOrder.indexOf(attribute.minimumEvidenceReliability);
    
    const hasMinimumReliability = evidence.some(e => {
      const evidenceIndex = reliabilityOrder.indexOf(e.reliability);
      return evidenceIndex >= minIndex;
    });
    
    if (!hasMinimumReliability) {
      const message = `Evidence for "${attribute.label}" must have reliability of at least "${attribute.minimumEvidenceReliability}"`;
      if (requirement === 'required' && attribute.blockWithoutEvidence) {
        result.passed = false;
        result.errors.push(message);
      } else {
        result.warnings.push(message);
      }
    }
  }
  
  return result;
}

const PROPOSALS_TABLE = 'value_proposals';
const ACCEPTED_VALUES_TABLE = 'accepted_values';
const EXCEPTIONS_TABLE = 'accepted_exceptions';

// ============================================================================
// Store State Type
// ============================================================================

interface ValueProposalState {
  // Data
  proposals: ValueProposal[];
  acceptedValues: AcceptedValue[];
  exceptions: AcceptedException[];
  /** Changes that took an official value away — what each current value replaced (lib/valueStatus). */
  replacements: ValueReplacement[];
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  
  // ================================
  // Proposal Actions
  // ================================
  
  proposeValue: (params: {
    itemId: string;
    attributeKey: string;
    proposedValue: unknown;
    proposedBy: string;
    proposedByName?: string;
    proposerRole: string;
    rationale?: string;
    evidence?: string;
    confidence?: number;
    /** Whether this proposal is a soft estimate or an authoritative score. Defaults to 'score'. */
    valueKind?: ValueKind;
    cycleId?: string; // For cycle-scoped permission checks
    trackId?: string; // Required for DB (value_proposals.track_id NOT NULL)
    /** Links this proposal to a structured InputRound, if any. */
    roundId?: string | null;
    /** Measurement scale (attributes.scales[].id); required for correct accept behavior when an attribute has multiple scales. */
    scaleId?: string | null;
    /** The session the contribution was made in (value_proposals.session_id). */
    sessionId?: string | null;
    /** value_proposals.source — 'session' for session entry; defaults to the column default ('user'). */
    source?: string;
  }) => Promise<ValueProposal>;
  
  withdrawProposal: (proposalId: string, withdrawnBy: string) => Promise<void>;
  
  // ================================
  // Acceptance Actions
  // ================================
  
  acceptProposal: (params: {
    proposalId: string;
    acceptedBy: string;
    acceptorRole: string;
    acceptanceMethod: AcceptanceMethod;
    acceptanceNote?: string;
    sessionScope?: string;
    cycleId?: string; // For cycle-scoped permission checks
  }) => Promise<AcceptedValue>;
  
  rejectProposal: (params: {
    proposalId: string;
    rejectedBy: string;
    reason: string;
  }) => Promise<void>;
  
  acceptValueDirectly: (params: {
    itemId: string;
    attributeKey: string;
    value: unknown;
    acceptedBy: string;
    acceptanceMethod: AcceptanceMethod;
    acceptanceNote?: string;
    sessionScope?: string;
    /** When attribute has multiple scales, each scale has its own value. Pass scale id so values do not overwrite each other. */
    scaleId?: string | null;
    /** Whether this accepted value is an estimate or a definitive score. Defaults to 'score'. */
    valueKind?: ValueKind;
    /** The contribution this value was taken from, when there is one (provenance). */
    acceptedFromProposalId?: string;
  }) => Promise<{ value: AcceptedValue; saved: boolean; error?: string }>;
  
  /** Remove an accepted value (e.g. when deselecting a pill). */
  clearAcceptedValue: (params: {
    itemId: string;
    attributeKey: string;
    scaleId?: string | null;
    sessionScope?: string;
  }) => Promise<{ success: boolean; error?: string }>;

  /**
   * Aggregate multiple estimate proposals into a single AcceptedValue.
   * Computes the consensus value from the given proposal IDs using the specified method,
   * marks each source proposal as accepted, and writes one AcceptedValue row with
   * full multi-party metadata (contributor count, aggregation method, raw inputs).
   */
  aggregateProposals: (params: {
    proposalIds: string[];
    aggregationMethod: ValueAggregationMethod;
    acceptedBy: string;
    acceptorRole: string;
    acceptanceNote?: string;
    sessionScope?: string;
    scaleId?: string | null;
  }) => Promise<{ value: AcceptedValue; saved: boolean; error?: string }>;
  
  // ================================
  // Exception Actions
  // ================================
  
  acceptException: (params: {
    itemId: string;
    attributeKey: string;
    reason: string;
    scope: ExceptionScope;
    acceptedBy: string;
    sessionId?: string;
    riskNote?: string;
  }) => Promise<AcceptedException>;
  
  removeException: (itemId: string, attributeKey: string, sessionId?: string) => Promise<void>;

  // ================================
  // Lock Actions (with governance events)
  // ================================
  
  lockValue: (itemId: string, attributeKey: string, lockedBy: string, cycleId?: string, options?: { silent?: boolean }) => Promise<void>;
  unlockValue: (itemId: string, attributeKey: string, unlockedBy?: string, cycleId?: string) => Promise<void>;
  
  // ================================
  // Bulk Actions
  // ================================
  
  updateAcceptedValue: (
    itemId: string,
    attributeKey: string,
    newValue: unknown,
    updatedBy: string,
    reason: string
  ) => Promise<void>;
  
  // ================================
  // Queries
  // ================================
  
  getProposalsForItem: (itemId: string) => ValueProposal[];
  getProposalsForAttribute: (itemId: string, attributeKey: string) => ValueProposal[];
  getPendingProposals: (itemId: string) => ValueProposal[];
  getProposalById: (proposalId: string) => ValueProposal | undefined;
  getSourceProposalForAcceptedValue: (accepted: AcceptedValue) => ValueProposal | undefined;
  /** All proposals with valueKind === 'estimate' for an (item, attribute), ordered newest first. */
  getEstimatesForAttribute: (itemId: string, attributeKey: string) => ValueProposal[];
  /** All proposals with valueKind === 'score' for an (item, attribute), ordered newest first. */
  getScoresForAttribute: (itemId: string, attributeKey: string) => ValueProposal[];
  /**
   * Returns one contestation record per (item, attribute) pair for all items in itemIds
   * that have an aggregated estimate accepted value (contributorCount >= 2).
   * Keyed by itemId.
   */
  getContestationSummary: (itemIds: string[]) => Map<string, ContestationRecord[]>;

  getAcceptedValuesForItem: (itemId: string, sessionId?: string) => AcceptedValue[];
  /** All accepted values for one attribute (one per scale when scaleId is set). Use for "Impact: High on L/M/H, 3 on 1-10". */
  getAcceptedValuesForAttribute: (itemId: string, attributeKey: string, sessionId?: string) => AcceptedValue[];
  /** Single value for (item, attribute), optionally for a specific scale. When scaleId given, returns value for that scale only. */
  getAcceptedValue: (itemId: string, attributeKey: string, sessionId?: string, scaleId?: string | null) => AcceptedValue | undefined;
  
  getExceptionsForItem: (itemId: string, sessionId?: string) => AcceptedException[];
  hasException: (itemId: string, attributeKey: string, sessionId?: string) => boolean;
  
  isValueLocked: (itemId: string, attributeKey: string) => boolean;
  
  // ================================
  // Reconciliation Checks
  // ================================
  
  isReconciled: (itemId: string, requiredAttributes: string[], sessionId?: string) => boolean;
  getMissingValues: (itemId: string, requiredAttributes: string[], sessionId?: string) => string[];
  getReconciliationStatus: (itemId: string, requiredAttributes: string[], sessionId?: string) => {
    isReconciled: boolean;
    accepted: string[];
    excepted: string[];
    missing: string[];
    pending: string[];
  };
}


/**
 * The replacement a write just made, so the value mark is right before the next
 * load (the database records the same change in accepted_value_revisions).
 * Official values only.
 */
function replacementFor(
  state: ValueProposalState,
  itemId: string,
  attributeKey: string,
  scaleId: string | null | undefined,
  sessionScope: string | null | undefined,
  by: string,
): ValueReplacement[] {
  if (sessionScope) return state.replacements;
  const prior = state.acceptedValues.find(
    (av) => av.itemId === itemId && av.attributeKey === attributeKey && !av.sessionScope && (av.scaleId ?? null) === (scaleId ?? null),
  );
  if (!prior) return state.replacements;
  return [
    {
      itemId,
      attributeKey,
      scaleId: scaleId ?? null,
      oldValue: prior.value,
      oldFromProposalId: prior.acceptedFromProposalId ?? null,
      changedBy: by,
      changedAt: new Date().toISOString(),
    },
    ...state.replacements,
  ];
}

// ============================================================================
// Value Proposal Store
// ============================================================================

export const useValueProposalStore = create<ValueProposalState>()(
  (set, get) => ({
    proposals: [],
    acceptedValues: [],
    exceptions: [],
    replacements: [],
      syncState: createInitialSyncState(),
      
      // ================================
      // Supabase Sync
      // ================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[ValueProposalStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const workspaceId = getCurrentWorkspaceId();
        const [proposalsResult, acceptedResult, exceptionsResult, replacements] = await Promise.all([
          fetchAll<ValueProposal>(PROPOSALS_TABLE, { column: 'proposed_at', ascending: false }, { filterByWorkspace: true }),
          fetchAll<AcceptedValue>(ACCEPTED_VALUES_TABLE, { column: 'accepted_at', ascending: false }, { filterByWorkspace: true }),
          fetchAll<AcceptedException>(EXCEPTIONS_TABLE, { column: 'accepted_at', ascending: false }, { filterByWorkspace: true }),
          // Only the value mark reads these; a failed read leaves every mark as it was without them.
          workspaceId ? fetchValueReplacements(workspaceId).catch(() => [] as ValueReplacement[]) : Promise.resolve([] as ValueReplacement[]),
        ]);
        
        if (proposalsResult.error || acceptedResult.error || exceptionsResult.error) {
          const error = proposalsResult.error || acceptedResult.error || exceptionsResult.error;
          set({ syncState: { ...get().syncState, isLoading: false, syncError: error } });
          console.error('[ValueProposalStore] Failed to fetch from Supabase:', error);
          return;
        }
        
        const proposals = (proposalsResult.data || []).map((p: any) => {
          if (p.value !== undefined && p.proposedValue === undefined) {
            return { ...p, proposedValue: p.value };
          }
          return p;
        });
        set({ 
          proposals,
          acceptedValues: acceptedResult.data || [],
          exceptions: exceptionsResult.data || [],
          replacements,
          syncState: { ...get().syncState, isLoading: false, lastSyncAt: new Date(), syncError: null }
        });
        console.log(`[ValueProposalStore] Loaded data from Supabase`);
      },
      
      // ================================
      // Proposal Actions
      // ================================
      
      proposeValue: async (params) => {
        // Resolve role → capabilities once; attribute governance uses capabilities only.
        // Use the DB-authoritative resolvedCapabilityIds when available (loaded by userStore on
        // login). The static role-bundle lookup is a fallback for roles not yet in the DB.
        const governanceStore = useAttributeGovernanceStore.getState();
        const userStore = useUserStore.getState();
        const currentRole = userStore.currentRole;
        const assignedNames = userStore.assignedRoleNames ?? [];
        const roleIdsList = [currentRole, ...assignedNames]
          .filter((r): r is string => typeof r === 'string' && r.length > 0)
          .filter((r, i, a) => a.indexOf(r) === i);
        const staticCaps = resolveUserCapabilities(roleIdsList);
        // resolvedCapabilityIds is loaded from the DB on login and is authoritative.
        // Cast is safe: values come from the same CapabilityId enum stored in role_permissions.
        const dbCaps = (userStore.resolvedCapabilityIds ?? []) as CapabilityId[];
        const userCapabilities = [...new Set([...staticCaps, ...dbCaps])];

        if (!governanceStore.canProposeValue(params.attributeKey, userCapabilities, params.cycleId, params.trackId)) {
          const rangeAccepted = params.cycleId
            ? governanceStore.isRangeAccepted(params.cycleId, params.attributeKey, params.trackId)
            : true;
          const reason = !rangeAccepted
            ? `Value range for "${params.attributeKey}" has not been accepted for this cycle yet. Accept the scale in Criteria/Scales first.`
            : `You need the VALUE_ENTER capability to propose values for this attribute.`;
          const errorMessage = `Permission denied: ${reason}`;
          console.warn('[ValueProposalStore] Permission denied:', errorMessage);

          window.dispatchEvent(new CustomEvent('value-proposal-blocked', {
            detail: {
              itemId: params.itemId,
              attributeKey: params.attributeKey,
              errors: [errorMessage],
              warnings: [],
              reason: rangeAccepted ? 'permission_denied' : 'range_not_accepted',
            }
          }));

          throw new Error(errorMessage);
        }
        
        // Check if range is accepted for this attribute in the cycle (if cycle-scoped)
        if (params.cycleId && !governanceStore.isRangeAccepted(params.cycleId, params.attributeKey, params.trackId)) {
          const cycleConfig = governanceStore.getCycleConfig(params.cycleId, params.attributeKey, params.trackId);
          if (cycleConfig && cycleConfig.rangeStatus !== 'accepted') {
            const errorMessage = `Cannot propose value: Value range for "${params.attributeKey}" has not been accepted for this cycle yet. Current status: ${cycleConfig.rangeStatus}`;
            console.warn('[ValueProposalStore] Range not accepted:', errorMessage);
            
            window.dispatchEvent(new CustomEvent('value-proposal-blocked', {
              detail: {
                itemId: params.itemId,
                attributeKey: params.attributeKey,
                errors: [errorMessage],
                warnings: [],
                reason: 'range_not_accepted',
              }
            }));
            
            throw new Error(errorMessage);
          }
        }
        
        const effectiveConfig = governanceStore.getEffectiveConfig(params.attributeKey, params.cycleId, params.trackId);
        if (effectiveConfig?.collectionMethod === 'tool_result') {
          console.warn('[ValueProposalStore] Warning: Attribute configured for tool_result collection, but receiving manual proposal');
          window.dispatchEvent(new CustomEvent('value-proposal-warning', {
            detail: {
              itemId: params.itemId,
              attributeKey: params.attributeKey,
              warnings: ['This attribute is configured for tool-based value collection. Manual proposals may be overwritten by tool results.'],
            }
          }));
        }
        
        const evidenceCheck = checkEvidenceRequirements(params.itemId, params.attributeKey);
        if (effectiveConfig?.requiresEvidence && !params.evidence?.length) {
          evidenceCheck.passed = false;
          evidenceCheck.errors.push(`Attribute "${params.attributeKey}" requires evidence to propose a value`);
        }
        
        if (!evidenceCheck.passed) {
          const errorMessage = `Cannot propose value: ${evidenceCheck.errors.join('; ')}`;
          console.warn('[ValueProposalStore] Evidence requirement not met:', errorMessage);
          
          window.dispatchEvent(new CustomEvent('value-proposal-blocked', {
            detail: {
              itemId: params.itemId,
              attributeKey: params.attributeKey,
              errors: evidenceCheck.errors,
              warnings: evidenceCheck.warnings,
              reason: 'evidence_required',
            }
          }));
          
          throw new Error(errorMessage);
        }
        
        const policyDecision = checkPolicyForItem('values.propose', params.itemId, { attributeKey: params.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.propose:', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'proposeValue', message: `Cannot propose: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot propose value: ${reasonCode}`);
        }
        
        if (evidenceCheck.warnings.length > 0) {
          console.warn('[ValueProposalStore] Evidence warnings:', evidenceCheck.warnings);
          window.dispatchEvent(new CustomEvent('value-proposal-warning', {
            detail: {
              itemId: params.itemId,
              attributeKey: params.attributeKey,
              warnings: evidenceCheck.warnings,
            }
          }));
        }
        
        const proposal: ValueProposal = {
          id: generateUUID(),
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          scaleId: params.scaleId ?? null,
          proposedValue: params.proposedValue,
          proposedBy: params.proposedBy,
          proposedByName: params.proposedByName,
          proposedAt: new Date(),
          proposerRole: params.proposerRole,
          rationale: params.rationale,
          evidence: params.evidence,
          confidence: params.confidence,
          valueKind: params.valueKind ?? 'score',
          status: 'proposed',
          trackId: params.trackId,
          cycleId: params.cycleId,
          roundId: params.roundId ?? null,
          ...(params.sessionId ? { sessionId: params.sessionId } : {}),
          ...(params.source ? { source: params.source } : {}),
        };
        
        // DB-first: persist then update state (no false reality)
        if (!isSupabaseAvailable()) {
          const err = 'Database not available. Proposal was not saved.';
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: err, type: 'error' }
          }));
          throw new Error(err);
        }
        const toInsert = { ...proposal, trackId: proposal.trackId ?? params.trackId, cycleId: proposal.cycleId ?? params.cycleId };
        const result = await insertRecord<ValueProposal>(PROPOSALS_TABLE, toInsert);
        if (result.error) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save proposal: ${result.error}`, type: 'error' }
          }));
          throw new Error(result.error);
        }
        
        set((state) => ({
          proposals: [...state.proposals, proposal],
        }));
        
        useAuditStore.getState().logValueProposal({
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          proposedValue: params.proposedValue,
          proposerRole: params.proposerRole,
          rationale: params.rationale,
          confidence: params.confidence,
          actorId: params.proposedBy,
          actorRole: params.proposerRole,
        });
        
        return proposal;
      },
      
      withdrawProposal: async (proposalId, withdrawnBy) => {
        const proposal = get().proposals.find((p) => p.id === proposalId);
        if (!proposal) return;
        const policyDecision = checkPolicyForItem('values.propose', proposal.itemId, { attributeKey: proposal.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.propose (withdrawProposal):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'withdrawProposal', message: `Cannot withdraw: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot withdraw proposal: ${reasonCode}`);
        }
        if (!isSupabaseAvailable()) {
          const err = 'Database not available. Withdrawal was not saved.';
          window.dispatchEvent(new CustomEvent('store-warning', { detail: { message: err, type: 'error' } }));
          throw new Error(err);
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) throw new Error('Database client not available');
        // One act, one write path (migration 978): only the proposer, only while open.
        const { error: withdrawError } = await supabase.rpc('withdraw_value_proposal', {
          p_proposal_id: proposalId,
        });
        if (withdrawError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save withdrawal: ${withdrawError.message}`, type: 'error' }
          }));
          throw new Error(withdrawError.message);
        }
        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === proposalId
              ? { ...p, status: 'withdrawn' as ProposalStatus, reviewedBy: withdrawnBy, reviewedAt: new Date() }
              : p
          ),
        }));
      },
      
      // ================================
      // Acceptance Actions
      // ================================
      
      acceptProposal: async (params) => {
        const proposal = get().proposals.find((p) => p.id === params.proposalId);
        if (!proposal) {
          throw new Error(`Proposal ${params.proposalId} not found`);
        }

        // Resolve role → capabilities once; attribute governance uses capabilities only.
        // Prefer DB-loaded resolvedCapabilityIds (covers custom roles); fall back to static
        // bundle map (covers built-in admin roles when DB hasn't loaded yet).
        const governanceStore = useAttributeGovernanceStore.getState();
        const userStore = useUserStore.getState();
        const dbCapabilities = (userStore.resolvedCapabilityIds ?? []) as import('../types/prioritization/capabilityIds').CapabilityId[];
        const assignedNames = userStore.assignedRoleNames ?? [];
        const roleIdsList = [userStore.currentRole, ...assignedNames]
          .filter((r): r is string => typeof r === 'string' && r.length > 0)
          .filter((r, i, a) => a.indexOf(r) === i);
        const bundleCapabilities = resolveUserCapabilities(roleIdsList);
        const userCapabilities = [...new Set([...dbCapabilities, ...bundleCapabilities])] as import('../types/prioritization/capabilityIds').CapabilityId[];

        if (!governanceStore.canApproveValue(proposal.attributeKey, userCapabilities, params.cycleId, proposal.trackId)) {
          const errorMessage = `Permission denied: You need the VALUE_ACCEPT capability to approve values for "${proposal.attributeKey}".`;
          console.warn('[ValueProposalStore] Permission denied for acceptance:', errorMessage);

          window.dispatchEvent(new CustomEvent('value-acceptance-blocked', {
            detail: {
              proposalId: params.proposalId,
              itemId: proposal.itemId,
              attributeKey: proposal.attributeKey,
              errors: [errorMessage],
              reason: 'permission_denied',
            }
          }));

          throw new Error(errorMessage);
        }
        
        // Authorization guard: values.accept (write-surfaces)
        const policyDecision = checkPolicyForItem('values.accept', proposal.itemId, { attributeKey: proposal.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.accept:', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'acceptProposal', message: `Cannot accept: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot accept proposal: ${reasonCode}`);
        }
        
        // DB-first: persist then update state (no false reality)
        if (!isSupabaseAvailable()) {
          const err = 'Database not available. Acceptance was not saved.';
          window.dispatchEvent(new CustomEvent('store-warning', { detail: { message: err, type: 'error' } }));
          throw new Error(err);
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          throw new Error('Database client not available');
        }
        // One act, one write path (migration 958, build contract §2.3c): the
        // database replaces the accepted value for this item, attribute, scale
        // and session scope, marks the proposal accepted by the signed-in
        // person, and checks membership, values.accept and the agent's review
        // roles itself. The public API runs the same function as the key's person.
        const { data: accepted, error: acceptError } = await supabase.rpc('accept_value_proposal', {
          p_proposal_id: proposal.id,
          p_note: params.acceptanceNote ?? null,
          p_acceptance_method: params.acceptanceMethod,
          p_session_scope: params.sessionScope ?? null,
        });
        if (acceptError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save acceptance: ${acceptError.message}`, type: 'error' }
          }));
          throw new Error(acceptError.message);
        }
        const savedRow = (accepted as { accepted_value?: Record<string, unknown> } | null)?.accepted_value ?? {};
        const valueKind: ValueKind = proposal.valueKind === 'estimate' ? 'estimate' : 'score';
        const acceptedValue: AcceptedValue = {
          id: typeof savedRow.id === 'string' ? savedRow.id : generateUUID(),
          itemId: proposal.itemId,
          attributeKey: proposal.attributeKey,
          scaleId: proposal.scaleId ?? undefined,
          value: proposal.proposedValue,
          acceptedFromProposalId: proposal.id,
          acceptedBy: params.acceptedBy,
          acceptedAt: typeof savedRow.accepted_at === 'string' ? new Date(savedRow.accepted_at) : new Date(),
          acceptanceMethod: params.acceptanceMethod,
          acceptanceNote: params.acceptanceNote,
          sessionScope: params.sessionScope,
          valueKind,
        };

        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === params.proposalId
              ? { ...p, status: 'accepted' as ProposalStatus, reviewedBy: params.acceptedBy, reviewedAt: new Date() }
              : p
          ),
          replacements: replacementFor(state, proposal.itemId, proposal.attributeKey, proposal.scaleId, params.sessionScope, params.acceptedBy),
          acceptedValues: [
            ...state.acceptedValues.filter(
              (av) => !(
                av.itemId === proposal.itemId &&
                av.attributeKey === proposal.attributeKey &&
                (av.sessionScope ?? undefined) === (params.sessionScope ?? undefined) &&
                (av.scaleId ?? null) === (proposal.scaleId ?? null)
              )
            ),
            acceptedValue,
          ],
          exceptions: state.exceptions.filter(
            (e) => !(e.itemId === proposal.itemId &&
                    e.attributeKey === proposal.attributeKey &&
                    (params.sessionScope ? e.sessionId === params.sessionScope : e.scope === 'global'))
          ),
        }));
        
        useAuditStore.getState().logValueAcceptance({
          itemId: proposal.itemId,
          attributeKey: proposal.attributeKey,
          acceptedValue: proposal.proposedValue,
          proposalId: proposal.id,
          acceptanceMethod: params.acceptanceMethod,
          sessionId: params.sessionScope,
          note: params.acceptanceNote,
          actorId: params.acceptedBy,
          actorRole: params.acceptorRole || 'unknown',
        });
        
        return acceptedValue;
      },
      
      rejectProposal: async (params) => {
        const proposal = get().proposals.find((p) => p.id === params.proposalId);
        if (!proposal) {
          throw new Error(`Proposal ${params.proposalId} not found`);
        }
        const policyDecision = checkPolicyForItem('values.reject', proposal.itemId, { attributeKey: proposal.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.reject:', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'rejectProposal', message: `Cannot reject: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot reject proposal: ${reasonCode}`);
        }
        if (!isSupabaseAvailable()) {
          const err = 'Database not available. Rejection was not saved.';
          window.dispatchEvent(new CustomEvent('store-warning', { detail: { message: err, type: 'error' } }));
          throw new Error(err);
        }
        // One act, one write path (migration 978): the database records the signed-in
        // person as rejecting and checks membership and values.accept itself; the iPhone
        // app calls the same function. The agent learns from the reason: migration 821
        // copies it onto its decision record.
        const supabase = getSafeSupabaseClient();
        if (!supabase) throw new Error('Database client not available');
        const { error: rejectError } = await supabase.rpc('reject_value_proposal', {
          p_proposal_id: params.proposalId,
          p_reason: params.reason ?? null,
        });
        if (rejectError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save rejection: ${rejectError.message}`, type: 'error' }
          }));
          throw new Error(rejectError.message);
        }
        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === params.proposalId
              ? {
                  ...p,
                  status: 'rejected' as ProposalStatus,
                  reviewedBy: params.rejectedBy,
                  reviewedAt: new Date(),
                  reviewNote: params.reason,
                }
              : p
          ),
        }));
        
        useAuditStore.getState().logValueRejection({
          itemId: proposal.itemId,
          attributeKey: proposal.attributeKey,
          rejectedValue: proposal.proposedValue,
          proposalId: proposal.id,
          reason: params.reason,
          actorId: params.rejectedBy,
          actorRole: 'workspace-admin',
        });
      },
      
      acceptValueDirectly: async (params) => {
        const policyDecision = checkPolicyForItem('values.accept', params.itemId, { attributeKey: params.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.accept (acceptValueDirectly):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'acceptValueDirectly', message: `Cannot accept value: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot accept value directly: ${reasonCode}`);
        }
        console.log('[ValueProposalStore] acceptValueDirectly called with:', {
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          value: params.value,
          sessionScope: params.sessionScope,
        });
        
        const acceptedValue: AcceptedValue = {
          id: generateUUID(),
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          scaleId: params.scaleId ?? undefined,
          value: params.value,
          acceptedBy: params.acceptedBy,
          acceptedAt: new Date(),
          acceptanceMethod: params.acceptanceMethod,
          acceptanceNote: params.acceptanceNote,
          sessionScope: params.sessionScope,
          valueKind: params.valueKind ?? 'score',
          acceptedFromProposalId: params.acceptedFromProposalId,
        };

        // Persist first; only update local state on DB success (Build Contract: no false reality).
        if (!isSupabaseAvailable()) {
          console.error('[ValueProposalStore] CRITICAL: Supabase is NOT available! Value will NOT be persisted!');
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: 'Database not available - values will not be saved!', type: 'error' }
          }));
          return { value: acceptedValue, saved: false, error: 'Database not available' };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          console.error('[ValueProposalStore] CRITICAL: Could not get Supabase client!');
          return { value: acceptedValue, saved: false, error: 'Could not get database client' };
        }

        // One act, one write path (migration 962, build contract §2.3c): the
        // database replaces the value for this item, attribute, scale and
        // session scope as the signed-in person, and checks membership and
        // values.accept / values.override itself. The iPhone app calls the same
        // function.
        let insertError: string | null = null;
        try {
          const { data: saved, error: setError } = await supabase.rpc('set_accepted_value', {
            p_item_id: acceptedValue.itemId,
            p_attribute_key: acceptedValue.attributeKey,
            p_value: acceptedValue.value,
            p_scale_id: acceptedValue.scaleId ?? null,
            p_session_scope: acceptedValue.sessionScope || null,
            p_acceptance_method: acceptedValue.acceptanceMethod,
            p_note: acceptedValue.acceptanceNote || null,
            p_value_kind: acceptedValue.valueKind ?? 'score',
            p_accepted_from_proposal_id: acceptedValue.acceptedFromProposalId ?? null,
          });
          if (setError) {
            insertError = setError.message;
          } else {
            const savedRow = (saved ?? {}) as Record<string, unknown>;
            if (typeof savedRow.id === 'string') acceptedValue.id = savedRow.id;
            if (typeof savedRow.accepted_at === 'string') acceptedValue.acceptedAt = new Date(savedRow.accepted_at);
            // The server records the signed-in person, whatever the caller passed.
            if (typeof savedRow.accepted_by === 'string') acceptedValue.acceptedBy = savedRow.accepted_by;
          }
        } catch (err) {
          insertError = err instanceof Error ? err.message : 'Unknown error';
          console.error('[ValueProposalStore] UNEXPECTED ERROR during save:', err);
        }

        if (insertError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save value: ${insertError}`, type: 'error' }
          }));
          return { value: acceptedValue, saved: false, error: insertError };
        }

        // DB success: now update canonical local state so UI reflects durable truth (one row per scale)
        set((state) => ({
          replacements: replacementFor(state, params.itemId, params.attributeKey, params.scaleId, params.sessionScope, params.acceptedBy),
          acceptedValues: [
            ...state.acceptedValues.filter(
              (av) => !(av.itemId === params.itemId &&
                       av.attributeKey === params.attributeKey &&
                       av.sessionScope === params.sessionScope &&
                       (av.scaleId ?? null) === (params.scaleId ?? null))
            ),
            acceptedValue,
          ],
        }));

        // Log audit event (item trail → audit_events via auditStore)
        useAuditStore.getState().logValueAcceptance({
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          acceptedValue: params.value,
          acceptanceMethod: params.acceptanceMethod,
          sessionId: params.sessionScope,
          note: params.acceptanceNote,
          actorId: params.acceptedBy,
          actorRole: 'workspace-admin',
        });
        
        return { value: acceptedValue, saved: true };
      },
      
      clearAcceptedValue: async (params) => {
        const policyDecision = checkPolicyForItem('values.accept', params.itemId, { attributeKey: params.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          return { success: false, error: `Cannot clear value: ${reasonCode}` };
        }
        if (!isSupabaseAvailable()) {
          return { success: false, error: 'Database not available' };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) return { success: false, error: 'Could not get database client' };

        // The same act as the iPhone app's clear (migration 962): the database
        // checks membership and values.accept / values.override, and the
        // history row keeps what was cleared.
        const { error } = await supabase.rpc('clear_accepted_value', {
          p_item_id: params.itemId,
          p_attribute_key: params.attributeKey,
          p_scale_id: params.scaleId != null && params.scaleId !== '' ? params.scaleId : null,
          p_session_scope: params.sessionScope || null,
        });
        if (error) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to clear value: ${error.message}`, type: 'error' }
          }));
          return { success: false, error: error.message };
        }
        // Clearing an accepted value is a change to the item; record it (2026-10-15).
        const cleared = get().acceptedValues.find(
          (av) => av.itemId === params.itemId && av.attributeKey === params.attributeKey &&
            (av.sessionScope ?? null) === (params.sessionScope ?? null) && (av.scaleId ?? null) === (params.scaleId ?? null),
        );
        void import('./itemsStore')
          .then(({ recordItemFieldChange }) =>
            recordItemFieldChange(params.itemId, 'acceptedValue', { attribute: params.attributeKey, value: cleared?.value ?? null }, null),
          )
          .catch(() => {});
        set((state) => ({
          acceptedValues: state.acceptedValues.filter(
            (av) => !(av.itemId === params.itemId &&
              av.attributeKey === params.attributeKey &&
              (av.sessionScope ?? null) === (params.sessionScope ?? null) &&
              (av.scaleId ?? null) === (params.scaleId ?? null))
          ),
        }));
        return { success: true };
      },

      aggregateProposals: async (params) => {
        const state = get();
        const proposals = params.proposalIds
          .map((id) => state.proposals.find((p) => p.id === id))
          .filter((p): p is ValueProposal => p !== undefined);

        if (proposals.length === 0) {
          return { value: {} as AcceptedValue, saved: false, error: 'No matching proposals found' };
        }

        const { itemId, attributeKey } = proposals[0];
        // Resolve scaleId: prefer explicit param, then derive from the proposals themselves.
        // This ensures the accepted value is stored under the correct scale and remains
        // findable by getAcceptedValue(itemId, attrKey, undefined, scale.id).
        const resolvedScaleId: string | null | undefined =
          params.scaleId !== undefined ? params.scaleId : (proposals[0].scaleId ?? null);

        const policyDecision = checkPolicyForItem('values.accept', itemId, { attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'aggregateProposals', message: `Cannot aggregate: ${reasonCode}`, decision: policyDecision },
          }));
          return { value: {} as AcceptedValue, saved: false, error: `Cannot aggregate proposals: ${reasonCode}` };
        }

        if (!isSupabaseAvailable()) {
          return { value: {} as AcceptedValue, saved: false, error: 'Database not available' };
        }

        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { value: {} as AcceptedValue, saved: false, error: 'Could not get database client' };
        }

        // One act, one write path (migration 978, build contract §2.3c): the database
        // combines the estimates (mean weighted by confidence, median, mode, otherwise
        // the latest), replaces the official value for this item, attribute, scale and
        // session scope, and marks the contributions accepted, as the signed-in person.
        const { data: saved, error: combineError } = await supabase.rpc('combine_value_proposals', {
          p_proposal_ids: proposals.map((p) => p.id),
          p_method: params.aggregationMethod,
          p_scale_id: resolvedScaleId ?? null,
          p_session_scope: params.sessionScope ?? null,
          p_note: params.acceptanceNote ?? null,
        });
        if (combineError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save aggregated value: ${combineError.message}`, type: 'error' }
          }));
          return { value: {} as AcceptedValue, saved: false, error: combineError.message };
        }

        const row = (saved ?? {}) as Record<string, unknown>;
        const consensusValue = row.value;
        const numberOrUndefined = (v: unknown) => (v === null || v === undefined ? undefined : Number(v));
        const acceptedValue: AcceptedValue = {
          id: String(row.id),
          itemId,
          attributeKey,
          scaleId: resolvedScaleId ?? undefined,
          value: consensusValue,
          acceptedBy: typeof row.accepted_by === 'string' ? row.accepted_by : params.acceptedBy,
          acceptedAt: typeof row.accepted_at === 'string' ? new Date(row.accepted_at) : new Date(),
          acceptanceMethod: 'consensus',
          acceptanceNote: params.acceptanceNote,
          sessionScope: params.sessionScope,
          valueKind: 'estimate',
          contributorCount: proposals.length,
          aggregationMethod: params.aggregationMethod,
          aggregationInputs: row.aggregation_inputs as AggregatedValueInput[] | undefined,
          spread: numberOrUndefined(row.spread),
          stdDev: numberOrUndefined(row.std_dev),
          isContested: typeof row.is_contested === 'boolean' ? row.is_contested : undefined,
        };

        set((state) => ({
          proposals: state.proposals.map((p) =>
            params.proposalIds.includes(p.id)
              ? { ...p, status: 'accepted' as ProposalStatus, reviewedBy: params.acceptedBy, reviewedAt: new Date() }
              : p
          ),
          acceptedValues: [
            ...state.acceptedValues.filter(
              (av) =>
                !(
                  av.itemId === itemId &&
                  av.attributeKey === attributeKey &&
                  (av.sessionScope ?? undefined) === (params.sessionScope ?? undefined) &&
                  (av.scaleId ?? null) === (resolvedScaleId ?? null)
                )
            ),
            acceptedValue,
          ],
        }));

        useAuditStore.getState().logValueAcceptance({
          itemId,
          attributeKey,
          acceptedValue: consensusValue,
          acceptanceMethod: 'consensus',
          sessionId: params.sessionScope,
          note: `Aggregated (${params.aggregationMethod}) from ${proposals.length} estimates`,
          actorId: params.acceptedBy,
          actorRole: params.acceptorRole,
        });

        return { value: acceptedValue, saved: true };
      },
      
      // ================================
      // Exception Actions
      // ================================
      
      acceptException: async (params) => {
        const policyDecision = checkPolicyForItem('values.accept', params.itemId, { attributeKey: params.attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.accept (acceptException):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'acceptException', message: `Cannot accept exception: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot accept exception: ${reasonCode}`);
        }
        const exception: AcceptedException = {
          id: generateUUID(),
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          reason: params.reason,
          scope: params.scope,
          sessionId: params.sessionId,
          acceptedBy: params.acceptedBy,
          acceptedAt: new Date(),
          riskNote: params.riskNote,
        };
        
        // DB-first: persist then update state (no false reality)
        if (!isSupabaseAvailable()) {
          const err = 'Database not available. Exception was not saved.';
          window.dispatchEvent(new CustomEvent('store-warning', { detail: { message: err, type: 'error' } }));
          throw new Error(err);
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          throw new Error('Database client not available');
        }
        const exceptionNow = new Date().toISOString();
        const dbRecord = {
          id: exception.id,
          item_id: exception.itemId,
          attribute_key: exception.attributeKey,
          reason: exception.reason,
          scope: exception.scope,
          session_id: exception.sessionId || null,
          accepted_by: exception.acceptedBy,
          accepted_at: exception.acceptedAt.toISOString(),
          risk_note: exception.riskNote || null,
          workspace_id: getCurrentWorkspaceId() || null,
          created_at: exceptionNow,
          updated_at: exceptionNow,
        };
        const { error: insertError } = await supabase.from(EXCEPTIONS_TABLE).insert(dbRecord).select();
        if (insertError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to save exception: ${insertError.message}`, type: 'error' }
          }));
          throw new Error(insertError.message);
        }
        
        set((state) => ({
          exceptions: [
            ...state.exceptions.filter(
              (e) => !(e.itemId === params.itemId &&
                      e.attributeKey === params.attributeKey &&
                      (params.scope === 'session' ? e.sessionId === params.sessionId : e.scope === 'global'))
            ),
            exception,
          ],
        }));
        
        useAuditStore.getState().logExceptionAcceptance({
          itemId: params.itemId,
          attributeKey: params.attributeKey,
          reason: params.reason,
          scope: params.scope,
          sessionId: params.sessionId,
          actorId: params.acceptedBy,
          actorRole: 'workspace-admin',
        });
        
        return exception;
      },
      
      removeException: async (itemId, attributeKey, sessionId) => {
        const policyDecision = checkPolicyForItem('values.reject', itemId, { attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.reject (removeException):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'removeException', message: `Cannot remove exception: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot remove exception: ${reasonCode}`);
        }
        const toRemove = get().exceptions.filter(
          (e) => e.itemId === itemId && e.attributeKey === attributeKey && (sessionId ? e.sessionId === sessionId : e.scope === 'global')
        );
        if (toRemove.length === 0) return;
        if (!isSupabaseAvailable()) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: 'Database not available. Exception was not removed.', type: 'error' },
          }));
          throw new Error('Database not available');
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) throw new Error('Database client not available');
        const ids = toRemove.map((e) => e.id);
        const { error } = await supabase.from(EXCEPTIONS_TABLE).delete().in('id', ids);
        if (error) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to remove exception: ${error.message}`, type: 'error' },
          }));
          throw new Error(error.message);
        }
        set((state) => ({
          exceptions: state.exceptions.filter(
            (e) => !(e.itemId === itemId && e.attributeKey === attributeKey && (sessionId ? e.sessionId === sessionId : e.scope === 'global'))
          ),
        }));
      },
      
      // ================================
      // Lock Actions
      // ================================
      
      lockValue: async (itemId, attributeKey, lockedBy, _cycleId?: string, options?: { silent?: boolean }) => {
        const policyDecision = checkPolicyForItem('values.override', itemId, { attributeKey });
        if (!policyDecision.allowed) {
          if (!options?.silent) {
            const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
            console.warn('[ValueProposalStore] Policy denied for values.override (lockValue):', reasonCode);
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'valueProposal', action: 'lockValue', message: `Cannot lock value: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const existing = get().acceptedValues.find(
          (av) => av.itemId === itemId && av.attributeKey === attributeKey && !av.sessionScope
        );
        if (!existing) return;
        const now = new Date();
        if (!isSupabaseAvailable()) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: 'Database not available. Value was not locked.', type: 'error' },
          }));
          return;
        }
        // One act, one write path (migration 978): needs values.override; the database
        // records the signed-in person as the one who locked it.
        const supabase = getSafeSupabaseClient();
        const { error: lockError } = supabase
          ? await supabase.rpc('lock_accepted_value', { p_accepted_value_id: existing.id })
          : { error: { message: 'Database client not available' } };
        if (lockError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to lock value: ${lockError.message}`, type: 'error' },
          }));
          return;
        }
        set((state) => ({
          acceptedValues: state.acceptedValues.map((av) =>
            av.itemId === itemId && av.attributeKey === attributeKey ? { ...av, lockedAt: now, lockedBy } : av
          ),
        }));
        try {
          useAuditStore.getState().logLock({
            type: 'lock',
            itemId,
            actorId: lockedBy,
            actorRole: 'facilitator',
            attributeKey,
            reason: 'Value locked',
          });
        } catch (_) { /* best-effort */ }
      },
      
      unlockValue: async (itemId, attributeKey, unlockedBy?: string, _cycleId?: string) => {
        const policyDecision = checkPolicyForItem('values.override', itemId, { attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.override (unlockValue):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'unlockValue', message: `Cannot unlock value: ${reasonCode}`, decision: policyDecision },
          }));
          return;
        }
        const existing = get().acceptedValues.find(
          (av) => av.itemId === itemId && av.attributeKey === attributeKey && !av.sessionScope
        );
        if (!existing) return;
        if (!isSupabaseAvailable()) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: 'Database not available. Value was not unlocked.', type: 'error' },
          }));
          return;
        }
        // One act, one write path (migration 978): needs values.override.
        const supabase = getSafeSupabaseClient();
        const { error: unlockError } = supabase
          ? await supabase.rpc('unlock_accepted_value', { p_accepted_value_id: existing.id })
          : { error: { message: 'Database client not available' } };
        if (unlockError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to unlock value: ${unlockError.message}`, type: 'error' },
          }));
          return;
        }
        set((state) => ({
          acceptedValues: state.acceptedValues.map((av) =>
            av.itemId === itemId && av.attributeKey === attributeKey ? { ...av, lockedAt: undefined, lockedBy: undefined } : av
          ),
        }));
        try {
          useAuditStore.getState().logLock({
            type: 'unlock',
            itemId,
            actorId: unlockedBy || 'system',
            actorRole: 'facilitator',
            attributeKey,
            reason: 'Value unlocked',
          });
        } catch (_) { /* best-effort */ }
      },
      
      // ================================
      // Bulk Actions
      // ================================
      
      updateAcceptedValue: async (itemId, attributeKey, newValue, updatedBy, reason) => {
        const existing = get().acceptedValues.find(
          (av) => av.itemId === itemId && av.attributeKey === attributeKey && !av.sessionScope
        );
        if (!existing) throw new Error(`No accepted value found for ${itemId}/${attributeKey}`);
        if (existing.lockedAt) throw new Error(`Value is locked and cannot be updated`);
        const policyDecision = checkPolicyForItem('values.override', itemId, { attributeKey });
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ValueProposalStore] Policy denied for values.override:', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'valueProposal', action: 'updateAcceptedValue', message: `Cannot override value: ${reasonCode}`, decision: policyDecision },
          }));
          throw new Error(`Cannot update accepted value: ${reasonCode}`);
        }
        const now = new Date();
        if (!isSupabaseAvailable()) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: 'Database not available. Value was not updated.', type: 'error' },
          }));
          throw new Error('Database not available');
        }
        // One act, one write path (migration 978): needs values.override, refused
        // while the value is locked, and recorded as the signed-in person.
        const supabase = getSafeSupabaseClient();
        if (!supabase) throw new Error('Database client not available');
        const { error: changeError } = await supabase.rpc('change_accepted_value', {
          p_accepted_value_id: existing.id,
          p_value: newValue,
          p_reason: reason ?? null,
        });
        if (changeError) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { message: `Failed to update value: ${changeError.message}`, type: 'error' },
          }));
          throw new Error(changeError.message);
        }
        set((state) => ({
          replacements: replacementFor(state, itemId, attributeKey, existing.scaleId, null, updatedBy),
          acceptedValues: state.acceptedValues.map((av) =>
            av.itemId === itemId && av.attributeKey === attributeKey && !av.sessionScope
              ? { ...av, value: newValue, acceptedBy: updatedBy, acceptedAt: now, acceptanceNote: reason }
              : av
          ),
        }));
      },
      
      // ================================
      // Queries
      // ================================
      
      getProposalsForItem: (itemId) => {
        return get().proposals.filter((p) => p.itemId === itemId);
      },
      
      getProposalsForAttribute: (itemId, attributeKey) => {
        return get().proposals.filter(
          (p) => p.itemId === itemId && p.attributeKey === attributeKey
        );
      },
      
      getPendingProposals: (itemId) => {
        return get().proposals.filter(
          (p) => p.itemId === itemId && p.status === 'proposed'
        );
      },

      getProposalById: (proposalId) => {
        return get().proposals.find((p) => p.id === proposalId);
      },

      getSourceProposalForAcceptedValue: (accepted) => {
        const state = get();
        if (accepted.acceptedFromProposalId) {
          const byId = state.proposals.find((p) => p.id === accepted.acceptedFromProposalId);
          if (byId) return byId;
        }
        const acceptedForAttr = state.proposals.filter(
          (p) =>
            p.itemId === accepted.itemId &&
            p.attributeKey === accepted.attributeKey &&
            p.status === 'accepted'
        );
        const valueMatch = acceptedForAttr.find(
          (p) => p.proposedValue === accepted.value || String(p.proposedValue) === String(accepted.value)
        );
        if (valueMatch) return valueMatch;
        const byDate = [...acceptedForAttr].sort(
          (a, b) => new Date(b.proposedAt).getTime() - new Date(a.proposedAt).getTime()
        );
        return byDate[0];
      },

      getEstimatesForAttribute: (itemId, attributeKey) => {
        return get()
          .proposals.filter(
            (p) =>
              p.itemId === itemId &&
              p.attributeKey === attributeKey &&
              p.valueKind === 'estimate'
          )
          .sort((a, b) => new Date(b.proposedAt).getTime() - new Date(a.proposedAt).getTime());
      },

      getScoresForAttribute: (itemId, attributeKey) => {
        return get()
          .proposals.filter(
            (p) =>
              p.itemId === itemId &&
              p.attributeKey === attributeKey &&
              (p.valueKind === 'score' || p.valueKind === undefined)
          )
          .sort((a, b) => new Date(b.proposedAt).getTime() - new Date(a.proposedAt).getTime());
      },

      getContestationSummary: (itemIds) => {
        const result = new Map<string, ContestationRecord[]>();
        const { acceptedValues } = get();

        for (const itemId of itemIds) {
          const records: ContestationRecord[] = [];
          const itemAVs = acceptedValues.filter(
            (av) => av.itemId === itemId && av.valueKind === 'estimate' && (av.contributorCount ?? 0) >= 2
          );
          for (const av of itemAVs) {
            const inputs = av.aggregationInputs ?? [];
            const numericVals = inputs
              .map((i) => Number(i.value))
              .filter((n) => !isNaN(n));
            const isNumeric = numericVals.length === inputs.length && inputs.length > 0;
            const distinctValueCount = isNumeric
              ? undefined
              : new Set(inputs.map((i) => String(i.value))).size;

            records.push({
              attributeKey: av.attributeKey,
              scaleId: av.scaleId ?? null,
              value: av.value,
              contributorCount: av.contributorCount ?? inputs.length,
              spread: av.spread,
              stdDev: av.stdDev,
              isContested: av.isContested ?? (av.stdDev !== undefined ? av.stdDev > 1.0 : false),
              isNumeric,
              distinctValueCount,
              acceptedValueId: av.id,
            });
          }
          if (records.length > 0) {
            result.set(itemId, records);
          }
        }
        return result;
      },

      // Official values always count; a session's own values count only inside
      // that session; another session's values never count (src/lib/valueScope.ts).
      getAcceptedValuesForItem: (itemId, sessionId) => {
        return valuesThatCount(get().acceptedValues.filter((av) => av.itemId === itemId), sessionId);
      },
      
      getAcceptedValuesForAttribute: (itemId, attributeKey, sessionId) => {
        return valuesThatCount(
          get().acceptedValues.filter((av) => av.itemId === itemId && av.attributeKey === attributeKey),
          sessionId,
        );
      },

      getAcceptedValue: (itemId, attributeKey, sessionId, scaleId) => {
        const values = get().acceptedValues.filter(
          (av) => av.itemId === itemId && av.attributeKey === attributeKey
        );
        if (sessionId) {
          const sessionSpecific = values.filter((av) => av.sessionScope === sessionId || !av.sessionScope);
          if (scaleId !== undefined && scaleId !== null) {
            return sessionSpecific.find((av) => (av.scaleId ?? null) === scaleId);
          }
          return sessionSpecific.find((av) => !av.sessionScope) ?? sessionSpecific[0];
        }
        const globalOnly = values.filter((av) => !av.sessionScope);
        if (scaleId !== undefined && scaleId !== null) {
          return globalOnly.find((av) => (av.scaleId ?? null) === scaleId);
        }
        return globalOnly.find((av) => (av.scaleId ?? null) === null) ?? globalOnly[0];
      },
      
      getExceptionsForItem: (itemId, sessionId) => {
        return get().exceptions.filter((e) => {
          if (e.itemId !== itemId) return false;
          if (sessionId) {
            return e.sessionId === sessionId || e.scope === 'global';
          }
          return e.scope === 'global';
        });
      },
      
      hasException: (itemId, attributeKey, sessionId) => {
        return get().exceptions.some((e) => {
          if (e.itemId !== itemId || e.attributeKey !== attributeKey) return false;
          if (sessionId) {
            return e.sessionId === sessionId || e.scope === 'global';
          }
          return e.scope === 'global';
        });
      },
      
      isValueLocked: (itemId, attributeKey) => {
        const value = get().acceptedValues.find(
          (av) => av.itemId === itemId && av.attributeKey === attributeKey && !av.sessionScope
        );
        return !!(value?.lockedAt);
      },
      
      // ================================
      // Reconciliation Checks
      // ================================
      
      isReconciled: (itemId, requiredAttributes, sessionId) => {
        const status = get().getReconciliationStatus(itemId, requiredAttributes, sessionId);
        return status.isReconciled;
      },
      
      getMissingValues: (itemId, requiredAttributes, sessionId) => {
        const status = get().getReconciliationStatus(itemId, requiredAttributes, sessionId);
        return status.missing;
      },
      
      getReconciliationStatus: (itemId, requiredAttributes, sessionId) => {
        const accepted: string[] = [];
        const excepted: string[] = [];
        const missing: string[] = [];
        const pending: string[] = [];
        
        for (const attrKey of requiredAttributes) {
          const hasAccepted = !!get().getAcceptedValue(itemId, attrKey, sessionId);
          const hasException = get().hasException(itemId, attrKey, sessionId);
          const hasPending = get().proposals.some(
            (p) => p.itemId === itemId && p.attributeKey === attrKey && p.status === 'proposed'
          );
          
          if (hasAccepted) {
            accepted.push(attrKey);
          } else if (hasException) {
            excepted.push(attrKey);
          } else if (hasPending) {
            pending.push(attrKey);
          } else {
            missing.push(attrKey);
          }
        }
        
        return {
        isReconciled: missing.length === 0 && pending.length === 0,
        accepted,
        excepted,
        missing,
        pending,
      };
    },
  })
);

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Get all proposals for an item
 */
export const useItemProposals = (itemId: string) =>
  useValueProposalStore((state) => state.getProposalsForItem(itemId));

/**
 * Get pending proposals for an item
 */
export const useItemPendingProposals = (itemId: string) =>
  useValueProposalStore((state) => state.getPendingProposals(itemId));

/**
 * Get accepted values for an item
 */
export const useItemAcceptedValues = (itemId: string, sessionId?: string) =>
  useValueProposalStore((state) => state.getAcceptedValuesForItem(itemId, sessionId));

/**
 * Get reconciliation status for an item
 */
export const useItemReconciliationStatus = (
  itemId: string, 
  requiredAttributes: string[], 
  sessionId?: string
) => useValueProposalStore((state) => 
  state.getReconciliationStatus(itemId, requiredAttributes, sessionId)
);
